import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Check, 
  ArrowRight, 
  Loader2,
  X,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const status = searchParams.get('status');

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      toast.error('Payment status check timed out. Please contact support.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sid}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Payment successful! Your website is now ordered.');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        toast.error('Payment session expired.');
        return;
      }

      setPaymentStatus('checking');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      toast.error('Error checking payment status.');
    }
  }, []);

  useEffect(() => {
    if (status === 'cancelled') {
      setPaymentStatus('cancelled');
      return;
    }
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setPaymentStatus('error');
    }
  }, [sessionId, status, pollPaymentStatus]);

  // Checking Payment Status View
  if (paymentStatus === 'checking') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4 pt-16 pb-16">
          <div className="max-w-md text-center">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-4">Verifying Payment...</h1>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment. This may take a few moments.
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Payment Success View
  if (paymentStatus === 'success') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4 pt-16 pb-16">
          <div className="max-w-lg text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Thank You for Your Order!</h1>
            <p className="text-muted-foreground mb-6">
              Your payment has been received and your website order is confirmed.
            </p>
            <div className="bg-card/50 border border-white/5 rounded-xl p-5 mb-6 text-left">
              <h3 className="font-semibold mb-3 text-primary">What happens next?</h3>
              <ol className="space-y-3 text-sm text-muted-foreground">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">1</span>
                  <span>We'll contact you within 24 hours with <strong className="text-foreground">domain name options</strong> for your website</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">2</span>
                  <span>Once you've chosen your domain, we'll set everything up</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">3</span>
                  <span>Your website goes <strong className="text-foreground">live</strong> - ready to attract new customers!</span>
                </li>
              </ol>
            </div>
            <div className="bg-card/50 border border-white/5 rounded-xl p-4 mb-6 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-medium capitalize">{paymentData?.plan_type?.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <span className="font-medium">£{paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : '0.00'}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-8">
              A confirmation email has been sent to your inbox.
            </p>
            <Link to="/">
              <Button className="gap-2">
                Back to TryWeb <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Payment Cancelled View
  if (paymentStatus === 'cancelled') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4 pt-16 pb-16">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-orange-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Payment Cancelled</h1>
            <p className="text-muted-foreground mb-6">
              No problem - your payment was cancelled and you haven't been charged. 
              Your website preview is still saved and ready whenever you'd like to proceed.
            </p>
            <div className="bg-card/50 border border-white/5 rounded-xl p-4 mb-6">
              <p className="text-sm text-muted-foreground">
                Have questions about our plans? We're happy to help you choose the right option for your business.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button variant="outline" className="gap-2 w-full sm:w-auto">
                  Contact Us
                </Button>
              </Link>
              <Link to="/">
                <Button className="gap-2 w-full sm:w-auto">
                  Back to Home <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Error/Timeout View
  return (
    <PageLayout>
      <div className="min-h-[60vh] flex items-center justify-center px-4 pt-16 pb-16">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold mb-4">
            {paymentStatus === 'timeout' ? 'Verification Timeout' : 'Something Went Wrong'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {paymentStatus === 'timeout' 
              ? 'We couldn\'t verify your payment status. If you completed the payment, please contact support.'
              : paymentStatus === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'There was an error processing your request. Please try again or contact support.'
            }
          </p>
          <div className="space-x-4">
            <Link to="/">
              <Button variant="outline" className="gap-2">
                Back to Home
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="gap-2">
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PaymentResult;
